import { jsPDF } from 'jspdf'

export const ANALYTICS_REPORT_SECTIONS = [
  'overview',
  'activity',
  'learning',
  'videos',
  'files',
  'onboarding',
  'surveys',
  'certificates',
  'users',
  'competitive',
  'invitations',
]

const LANGUAGE_LOCALES = {
  Chinese: 'zh-CN',
  English: 'en-US',
  German: 'de-DE',
  Hindi: 'hi-IN',
  Japanese: 'ja-JP',
  Portuguese: 'pt-BR',
  Spanish: 'es-ES',
}

const UNICODE_FONTS = {
  'cjk-content': { family: 'NotoSansAnalyticsCJK', file: 'NotoSansAnalyticsCJK.ttf', path: '/fonts/analytics/NotoSansAnalyticsCJK.ttf' },
  'hi-IN': { family: 'NotoSansAnalyticsHi', file: 'NotoSansAnalyticsHi.ttf', path: '/fonts/analytics/NotoSansAnalyticsHi.ttf' },
  'ja-JP': { family: 'NotoSansAnalyticsJa', file: 'NotoSansAnalyticsJa.ttf', path: '/fonts/analytics/NotoSansAnalyticsJa.ttf' },
  'zh-CN': { family: 'NotoSansAnalyticsZh', file: 'NotoSansAnalyticsZh.ttf', path: '/fonts/analytics/NotoSansAnalyticsZh.ttf' },
}

const COLORS = {
  background: [3, 8, 24],
  blue: [1, 60, 166],
  blueLight: [82, 146, 255],
  border: [214, 220, 232],
  ink: [19, 29, 48],
  muted: [93, 105, 126],
  pale: [242, 246, 252],
  white: [255, 255, 255],
}

const SECTION_CONFIG = {
  overview: {
    title: 'analytics_nav_overview',
    description: 'analytics_overview_description',
    kpis: [
      ['activeUsers', 'analytics_kpi_active_users', 'number'],
      ['sessions', 'analytics_kpi_sessions', 'number'],
      ['returningUsers', 'analytics_kpi_returning_users', 'number'],
      ['contentViews', 'analytics_kpi_content_views', 'number'],
      ['coursesCompleted', 'analytics_kpi_courses_completed', 'number'],
      ['videoViews', 'analytics_kpi_video_views', 'number'],
      ['downloads', 'analytics_kpi_downloads', 'number'],
      ['certificatesIssued', 'analytics_kpi_certificates_issued', 'number'],
      ['engagementRate', 'analytics_kpi_engagement_rate', 'percent'],
      ['aiInteractions', 'analytics_column_ai_interactions', 'number'],
    ],
    charts: [
      ['timeline', 'analytics_chart_usage_over_time', ['activeUsers', 'sessions', 'activities', 'events']],
      ['personaUsage', 'analytics_chart_usage_by_persona', ['viewed', 'started', 'completed', 'downloaded', 'other']],
      ['onboardingFunnel', 'analytics_chart_onboarding_funnel', ['value']],
      ['certificateJourney', 'analytics_chart_completion_to_certificate', ['value']],
      ['areaComparison', 'analytics_chart_area_comparison', ['activeUsers', 'sessions', 'contentViews', 'videoViews', 'downloads', 'certificates', 'aiInteractions']],
    ],
    tables: [
      ['competitiveHubUsage', 'analytics_hub_traffic', [
        ['sectionLabel', 'analytics_hub_page'],
        ['views', 'analytics_column_views', 'number'],
        ['uniqueViewers', 'analytics_column_unique_users', 'number'],
      ]],
      ['topContent', 'analytics_chart_most_used_content', [
        ['title', 'analytics_column_content'],
        ['area', 'analytics_column_area'],
        ['contentType', 'analytics_column_content_type'],
        ['views', 'analytics_column_views', 'number'],
        ['completionRate', 'analytics_column_completion', 'percent'],
      ]],
    ],
  },
  learning: {
    title: 'analytics_nav_learning',
    description: 'analytics_learning_description',
    kpis: [
      ['views', 'analytics_kpi_views', 'number'],
      ['uniqueViewers', 'analytics_kpi_unique_viewers', 'number'],
      ['started', 'analytics_kpi_courses_started', 'number'],
      ['completed', 'analytics_kpi_courses_completed', 'number'],
      ['completionRate', 'analytics_kpi_completion_rate', 'percent'],
      ['averageProgress', 'analytics_kpi_average_progress', 'percent'],
      ['certificatesIssued', 'analytics_kpi_certificates_issued', 'number'],
      ['certificationRate', 'analytics_kpi_certification_rate', 'percent'],
    ],
    charts: [
      ['viewsOverTime', 'analytics_chart_course_views_over_time', ['views', 'uniqueViewers', 'completed']],
      ['completionFunnel', 'analytics_chart_completion_funnel', ['value', 'users', 'count'], [
        'analytics_funnel_viewed',
        'analytics_funnel_started',
        'analytics_funnel_completed',
        'analytics_funnel_certified',
      ]],
    ],
    tables: [
      ['topCourses', 'analytics_chart_top_courses', [[['title', 'name'], 'analytics_column_content'], ['views', 'analytics_column_views', 'number']]],
      ['mostCompleted', 'analytics_chart_most_completed', [[['title', 'name'], 'analytics_column_content'], ['completed', 'analytics_column_completed', 'number']]],
      ['lowestCompletion', 'analytics_chart_lowest_completion', [[['title', 'name'], 'analytics_column_content'], ['completionRate', 'analytics_column_completion', 'percent']]],
      ['courseRows', 'analytics_table_courses', [
        [['title', 'courseTitle'], 'analytics_column_course'],
        ['area', 'analytics_column_area'],
        ['views', 'analytics_column_views', 'number'],
        ['uniqueUsers', 'analytics_column_unique_users', 'number'],
        ['started', 'analytics_column_started', 'number'],
        ['completed', 'analytics_column_completed', 'number'],
        ['completionRate', 'analytics_column_completion', 'percent'],
        ['certificates', 'analytics_column_certificates', 'number'],
      ]],
    ],
  },
  videos: {
    title: 'analytics_nav_videos',
    description: 'analytics_videos_description',
    kpis: [
      ['plays', 'analytics_kpi_video_plays', 'number'],
      ['uniqueViewers', 'analytics_kpi_unique_viewers', 'number'],
      ['averageWatchPercentage', 'analytics_kpi_average_watch_percentage', 'percent'],
      ['completedViews', 'analytics_kpi_completed_views', 'number'],
      ['completionRate', 'analytics_kpi_completion_rate', 'percent'],
    ],
    charts: [
      ['viewsOverTime', 'analytics_chart_video_views_over_time', ['plays', 'views', 'uniqueViewers']],
      ['retentionFunnel', 'analytics_chart_retention_funnel', ['value', 'viewers', 'count'], [
        'analytics_funnel_video_started',
        'analytics_funnel_reached_25',
        'analytics_funnel_reached_50',
        'analytics_funnel_reached_75',
        'analytics_funnel_video_completed',
      ]],
    ],
    tables: [
      ['mostWatched', 'analytics_most_watched', [[['title', 'name'], 'analytics_column_content'], ['plays', 'analytics_column_plays', 'number']]],
      ['highestCompletion', 'analytics_highest_completion', [[['title', 'name'], 'analytics_column_content'], ['completionRate', 'analytics_column_completion', 'percent']]],
      ['lowestCompletion', 'analytics_chart_lowest_completion', [[['title', 'name'], 'analytics_column_content'], ['completionRate', 'analytics_column_completion', 'percent']]],
      ['videoRows', 'analytics_table_videos', [
        [['title', 'videoTitle'], 'analytics_column_video'],
        ['area', 'analytics_column_area'],
        [['course', 'courseTitle'], 'analytics_column_course'],
        ['plays', 'analytics_column_plays', 'number'],
        ['uniqueViewers', 'analytics_column_unique_viewers', 'number'],
        ['averageWatchPercentage', 'analytics_column_average_watch', 'percent'],
        ['completionRate', 'analytics_column_completion', 'percent'],
      ]],
    ],
  },
  files: {
    title: 'analytics_nav_files',
    description: 'analytics_files_description',
    kpis: [
      ['downloads', 'analytics_kpi_downloads', 'number'],
      ['totalDownloads', 'analytics_kpi_downloads', 'number'],
      ['uniqueDownloaders', 'analytics_kpi_unique_downloaders', 'number'],
      ['filesWithoutDownloads', 'analytics_kpi_files_without_downloads', 'number'],
    ],
    charts: [
      ['downloadsOverTime', 'analytics_chart_downloads_over_time', ['downloads', 'uniqueDownloaders']],
    ],
    tables: [
      ['mostDownloaded', 'analytics_most_downloaded', [[['title', 'name'], 'analytics_column_content'], ['downloads', 'analytics_column_downloads', 'number']]],
      ['withoutDownloads', 'analytics_files_without_downloads', [[['title', 'name'], 'analytics_column_content'], ['downloads', 'analytics_column_downloads', 'number']]],
      ['fileRows', 'analytics_table_files', [
        [['title', 'fileTitle'], 'analytics_column_file'],
        ['area', 'analytics_column_area'],
        [['course', 'courseTitle'], 'analytics_column_course'],
        ['downloads', 'analytics_column_downloads', 'number'],
        [['uniqueUsers', 'uniqueDownloaders'], 'analytics_column_unique_users', 'number'],
        [['lastDownload', 'lastDownloadedAt'], 'analytics_column_last_download', 'dateTime'],
      ]],
    ],
  },
  onboarding: {
    title: 'analytics_nav_onboarding',
    description: 'analytics_onboarding_description',
    kpis: [
      ['eligible', 'analytics_kpi_eligible_users', 'number'],
      ['loggedIn', 'analytics_kpi_logged_in', 'number'],
      ['opened', 'analytics_kpi_onboarding_opened', 'number'],
      ['watched', 'analytics_kpi_onboarding_watched', 'number'],
      ['completed', 'analytics_kpi_onboarding_completed', 'number'],
      ['loggedInNotWatched', 'analytics_kpi_logged_in_not_watched', 'number'],
    ],
    charts: [
      ['funnel', 'analytics_chart_onboarding_funnel', ['value', 'users', 'count'], [
        'analytics_funnel_eligible',
        'analytics_funnel_logged_in',
        'analytics_funnel_opened',
        'analytics_funnel_watched',
        'analytics_funnel_completed',
      ]],
    ],
    tables: [
      ['noWatchUsers', 'analytics_logged_in_not_watched_title', [
        ['displayName', 'analytics_column_user'],
        ['persona', 'analytics_column_persona'],
        ['locale', 'analytics_column_locale'],
        [['lastLoginAt', 'lastLogin'], 'analytics_column_last_login', 'dateTime'],
      ]],
    ],
  },
  surveys: {
    title: 'analytics_nav_surveys',
    description: 'analytics_surveys_description',
    kpis: [
      ['totalSurveys', 'analytics_kpi_total_surveys', 'number'],
      ['surveyViews', 'analytics_kpi_survey_views', 'number'],
      ['surveyStarts', 'analytics_kpi_survey_starts', 'number'],
      ['surveyCompletions', 'analytics_kpi_survey_completions', 'number'],
      ['responses', 'analytics_kpi_survey_responses', 'number'],
      ['uniqueParticipants', 'analytics_kpi_unique_participants', 'number'],
      ['completionRate', 'analytics_kpi_completion_rate', 'percent'],
      ['abandonmentRate', 'analytics_kpi_abandonment_rate', 'percent'],
      ['averageCompletionTime', 'analytics_kpi_average_completion_time', 'duration'],
    ],
    charts: [
      ['timeline', 'analytics_chart_survey_usage_over_time', ['views', 'starts', 'completions']],
      ['funnel', 'analytics_chart_survey_funnel', ['value']],
    ],
    tables: [
      ['surveyRows', 'analytics_table_surveys', [
        ['survey', 'analytics_filter_survey'],
        ['area', 'analytics_column_area'],
        ['course', 'analytics_column_course'],
        ['views', 'analytics_kpi_survey_views', 'number'],
        ['starts', 'analytics_kpi_survey_starts', 'number'],
        ['completions', 'analytics_kpi_survey_completions', 'number'],
        ['responses', 'analytics_kpi_survey_responses', 'number'],
        ['completionRate', 'analytics_kpi_completion_rate', 'percent'],
        ['abandonmentRate', 'analytics_kpi_abandonment_rate', 'percent'],
      ]],
      ['questionAnalytics', 'analytics_table_survey_questions', [
        ['survey', 'analytics_filter_survey'],
        ['question', 'analytics_column_question'],
        ['type', 'analytics_column_question_type'],
        ['responseCount', 'analytics_kpi_survey_responses', 'number'],
        ['average', 'analytics_column_average', 'decimal'],
      ]],
      ['answerDistribution', 'analytics_table_survey_answers', [
        ['survey', 'analytics_filter_survey'],
        ['question', 'analytics_column_question'],
        ['option', 'analytics_column_answer_option'],
        ['count', 'analytics_total', 'number'],
        ['percentage', 'analytics_column_percentage', 'percent'],
      ]],
    ],
  },
  certificates: {
    title: 'analytics_nav_certificates',
    description: 'analytics_certificates_description',
    kpis: [
      ['issued', 'analytics_kpi_certificates_issued', 'number'],
      ['certificatesIssued', 'analytics_kpi_certificates_issued', 'number'],
      ['eligibleCompletions', 'analytics_kpi_eligible_completions', 'number'],
      ['certificationRate', 'analytics_kpi_certification_rate', 'percent'],
      ['automatic', 'analytics_kpi_automatic_certificates', 'number'],
      ['trainerApprovalCertificates', 'analytics_kpi_trainer_approval_certificates', 'number'],
      ['pending', 'analytics_kpi_pending_approvals', 'number'],
      ['approved', 'analytics_kpi_approved', 'number'],
      ['rejected', 'analytics_kpi_rejected', 'number'],
    ],
    charts: [
      ['statusBreakdown', 'analytics_chart_certificate_status', ['value', 'certificates', 'count']],
    ],
    tables: [
      ['certificateRows', 'analytics_table_certificates', [
        ['userName', 'analytics_column_user'],
        ['userEmail', 'analytics_column_email'],
        ['courseTitle', 'analytics_column_course'],
        ['program', 'analytics_column_program'],
        ['trainer', 'analytics_column_trainer'],
        ['area', 'analytics_column_area'],
        ['type', 'analytics_column_certificate_type'],
        ['status', 'analytics_column_status'],
        ['issuedAt', 'analytics_column_issued_date', 'dateTime'],
        ['certificateId', 'analytics_column_id'],
      ]],
    ],
  },
  users: {
    title: 'analytics_nav_users',
    description: 'analytics_users_description',
    kpis: [
      ['dau', 'analytics_kpi_dau', 'number'],
      ['wau', 'analytics_kpi_wau', 'number'],
      ['mau', 'analytics_kpi_mau', 'number'],
      ['activeUsers', 'analytics_kpi_active_users', 'number'],
      ['newUsers', 'analytics_kpi_new_users', 'number'],
      ['returningUsers', 'analytics_kpi_returning_users', 'number'],
      ['sessionsPerUser', 'analytics_kpi_sessions_per_user', 'decimal'],
      ['averageSessionDuration', 'analytics_kpi_average_session_duration', 'duration'],
      ['inactiveUsers', 'analytics_kpi_inactive_users', 'number'],
    ],
    charts: [
      ['activeUsersOverTime', 'analytics_chart_active_users_over_time', ['activeUsers', 'users']],
      ['newVsReturning', 'analytics_chart_new_vs_returning', ['value']],
      ['usageByPersona', 'analytics_chart_usage_by_persona', ['activeUsers', 'users']],
      ['usageByLocale', 'analytics_chart_usage_by_locale', ['activities', 'users', 'sessions']],
      ['activityDistribution', 'analytics_chart_activity_distribution', ['value', 'users', 'count']],
    ],
    tables: [
      ['mostActiveUsers', 'analytics_chart_most_active_users', [
        ['displayName', 'analytics_column_user'],
        ['persona', 'analytics_column_persona'],
        ['sessions', 'analytics_column_sessions', 'number'],
        ['activities', 'analytics_column_activities', 'number'],
        [['lastActiveAt', 'lastActive', 'lastAccess'], 'analytics_column_last_active', 'dateTime'],
      ]],
    ],
  },
  activity: {
    title: 'analytics_nav_activity',
    description: 'analytics_activity_description',
    kpis: [
      ['activeUsers', 'analytics_kpi_active_users', 'number'],
      ['sessions', 'analytics_kpi_sessions', 'number'],
      ['returningUsers', 'analytics_kpi_returning_users', 'number'],
      ['contentViews', 'analytics_kpi_content_views', 'number'],
      ['total', 'analytics_kpi_measured_activities', 'number'],
    ],
    charts: [
      ['timeline', 'analytics_chart_usage_over_time', ['activities', 'activeUsers']],
      ['personaUsage', 'analytics_chart_usage_by_persona', ['viewed', 'started', 'completed', 'downloaded', 'other']],
      ['activityBreakdown', 'analytics_chart_activity_by_type', ['count']],
      ['portalUsage', 'analytics_chart_activity_by_portal', ['activities']],
    ],
    tables: [],
  },
  competitive: {
    title: 'analytics_nav_competitive',
    description: 'analytics_competitive_description',
    kpis: [
      ['activeUsers', 'analytics_kpi_active_users', 'number'],
      ['sessions', 'analytics_kpi_sessions', 'number'],
      ['totalViews', 'analytics_kpi_total_views', 'number'],
      ['uniqueViews', 'analytics_kpi_unique_views', 'number'],
      ['searches', 'analytics_kpi_searches', 'number'],
      ['aiConversations', 'analytics_kpi_ai_conversations', 'number'],
      ['aiPrompts', 'analytics_kpi_ai_prompts', 'number'],
      ['sourceOpens', 'analytics_kpi_source_opens', 'number'],
      ['averageSessionDuration', 'analytics_kpi_average_session_duration', 'duration'],
    ],
    charts: [
      ['timeline', 'analytics_chart_competitive_usage_over_time', ['users', 'sessions', 'views', 'searches', 'aiUsage']],
      ['sectionUsage', 'analytics_chart_section_usage', ['views', 'sessions', 'engagement']],
      ['competitorRows', 'analytics_chart_competitor_interest', ['views', 'searches', 'aiInteractions', 'sourceOpens']],
      ['assistantRows', 'analytics_table_ai_assistants', ['sessions', 'prompts', 'responses', 'errors', 'citationOpens']],
    ],
    tables: [
      ['contentRows', 'analytics_table_competitive_content', [
        ['title', 'analytics_column_content'],
        ['contentType', 'analytics_column_content_type'],
        ['competitor', 'analytics_column_competitor'],
        ['source', 'analytics_column_source'],
        ['views', 'analytics_column_views', 'number'],
        ['uniqueViews', 'analytics_kpi_unique_views', 'number'],
        ['sourceOpens', 'analytics_kpi_source_opens', 'number'],
        ['aiInteractions', 'analytics_column_ai_interactions', 'number'],
      ]],
      ['assistantRows', 'analytics_table_ai_assistants', [
        ['assistantId', 'analytics_filter_assistant'],
        ['sessions', 'analytics_column_sessions', 'number'],
        ['prompts', 'analytics_kpi_ai_prompts', 'number'],
        ['responses', 'analytics_column_responses', 'number'],
        ['errors', 'analytics_column_errors', 'number'],
      ]],
    ],
  },
  invitations: {
    title: 'analytics_nav_invitations',
    description: 'analytics_invitations_description',
    kpis: [
      ['invitationsCreated', 'analytics_kpi_invitations_created', 'number'],
      ['invitationsUsed', 'analytics_kpi_invitations_used', 'number'],
      ['registrations', 'analytics_kpi_registrations', 'number'],
      ['firstAccesses', 'analytics_kpi_first_accesses', 'number'],
      ['activatedUsers', 'analytics_kpi_activated_users', 'number'],
      ['engagedUsers', 'analytics_kpi_engaged_users', 'number'],
      ['activationRate', 'analytics_kpi_activation_rate', 'percent'],
      ['engagementRate', 'analytics_kpi_engagement_rate', 'percent'],
    ],
    charts: [
      ['funnel', 'analytics_invitation_funnel', ['value']],
    ],
    tables: [
      ['performance', 'analytics_invitation_performance', [
        ['invitation', 'analytics_column_invitation'],
        ['area', 'analytics_column_area'],
        ['creator', 'analytics_column_creator'],
        ['status', 'analytics_column_status'],
        ['registrations', 'analytics_kpi_registrations', 'number'],
        ['activated', 'analytics_kpi_activated_users', 'number'],
        ['engaged', 'analytics_kpi_engaged_users', 'number'],
      ]],
    ],
  },
}

const cleanValue = value => value === null || value === undefined || value === '' ? null : value

const SERIES_LABEL_KEYS = {
  activeUsers: 'analytics_kpi_active_users',
  viewed: 'analytics_funnel_viewed',
  started: 'analytics_funnel_started',
  starts: 'analytics_kpi_survey_starts',
  completions: 'analytics_kpi_survey_completions',
  responses: 'analytics_kpi_survey_responses',
  downloaded: 'analytics_kpi_downloads',
  other: 'analytics_activity_other',
  activities: 'analytics_column_activities',
  certificates: 'analytics_column_certificates',
  contentViews: 'analytics_kpi_content_views',
  completed: 'analytics_column_completed',
  count: 'analytics_total',
  downloads: 'analytics_column_downloads',
  videoViews: 'analytics_kpi_video_views',
  aiInteractions: 'analytics_column_ai_interactions',
  events: 'analytics_column_activities',
  newUsers: 'analytics_kpi_new_users',
  plays: 'analytics_column_plays',
  returningUsers: 'analytics_kpi_returning_users',
  sessions: 'analytics_kpi_sessions',
  uniqueDownloaders: 'analytics_kpi_unique_downloaders',
  uniqueUsers: 'analytics_column_unique_users',
  uniqueViewers: 'analytics_kpi_unique_viewers',
  users: 'analytics_kpi_users',
  value: 'analytics_total',
  viewers: 'analytics_kpi_unique_viewers',
  views: 'analytics_column_views',
}

const SEMANTIC_LABEL_KEYS = {
  new: 'analytics_kpi_new_users',
  returning: 'analytics_kpi_returning_users',
  downloaded: 'analytics_kpi_downloads',
  loggedin: 'analytics_funnel_logged_in',
  sessionstarted: 'analytics_kpi_sessions',
  certificateissued: 'analytics_kpi_certificates_issued',
  universe: 'analytics_portal_universe',
  lite: 'analytics_portal_lite',
  accelerator: 'analytics_portal_accelerator',
  automatic: 'analytics_kpi_automatic_certificates',
  certified: 'analytics_funnel_certified',
  completed: 'analytics_funnel_completed',
  eligible: 'analytics_funnel_eligible',
  opened: 'analytics_funnel_opened',
  pending: 'analytics_kpi_pending_approvals',
  registered: 'analytics_funnel_registered',
  registeredeligible: 'analytics_funnel_eligible',
  rejected: 'analytics_kpi_rejected',
  started: 'analytics_funnel_started',
  trainerapproval: 'analytics_kpi_trainer_approval_certificates',
  viewed: 'analytics_funnel_viewed',
  views: 'analytics_funnel_viewed',
  watched: 'analytics_funnel_watched',
  approved: 'analytics_kpi_approved',
  created: 'analytics_funnel_invitation_created',
  used: 'analytics_funnel_invitation_used',
  firstaccess: 'analytics_funnel_first_access',
  activated: 'analytics_funnel_activated',
  engaged: 'analytics_funnel_engaged',
  '0': 'analytics_funnel_video_started',
  '25': 'analytics_funnel_reached_25',
  '50': 'analytics_funnel_reached_50',
  '75': 'analytics_funnel_reached_75',
  '100': 'analytics_funnel_video_completed',
}

const PERIOD_LABEL_KEYS = {
  today: 'analytics_filter_today',
  '7': 'analytics_filter_7_days',
  '7d': 'analytics_filter_7_days',
  '30': 'analytics_filter_30_days',
  '30d': 'analytics_filter_30_days',
  '90': 'analytics_filter_90_days',
  '90d': 'analytics_filter_90_days',
  year: 'analytics_filter_this_year',
  thisyear: 'analytics_filter_this_year',
  custom: 'analytics_filter_custom_range',
}

const TECHNICAL_FILTER_KEYS = new Set(['direction', 'page', 'pageSize', 'section', 'sort'])

const tr = (t, key, fallback) => {
  const value = typeof t === 'function' ? t(key) : null
  return value && value !== key ? value : fallback || key
}

export const resolveAnalyticsLocale = language => (
  LANGUAGE_LOCALES[language] || (typeof language === 'string' && language.includes('-') ? language : 'en-US')
)

const validDate = value => {
  if (cleanValue(value) === null) return null
  const date = new Date(/^\d{4}-\d{2}-\d{2}$/.test(String(value)) ? `${value}T12:00:00` : value)
  return Number.isNaN(date.getTime()) ? null : date
}

const makeFormatters = locale => ({
  date: value => !validDate(value)
    ? '-'
    : new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(validDate(value)),
  dateTime: value => !validDate(value)
    ? '-'
    : new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(validDate(value)),
  decimal: value => new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(Number(value || 0)),
  duration: value => {
    const seconds = Math.max(0, Math.round(Number(value || 0)))
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const remaining = seconds % 60
    return [hours ? `${hours}h` : null, minutes ? `${minutes}m` : null, `${remaining}s`].filter(Boolean).join(' ')
  },
  number: value => new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(Number(value || 0)),
  percent: value => new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 1 }).format(Number(value || 0) / 100),
})

const formatMetric = (formatters, value, type) => (
  formatters[type] ? formatters[type](value) : String(cleanValue(value) ?? '-')
)

const unwrapData = payload => {
  if (!payload || typeof payload !== 'object') return {}
  if (payload.data && typeof payload.data === 'object' && !Array.isArray(payload.data)) {
    return { ...payload, ...payload.data }
  }
  return payload
}

const KPI_ALIASES = {
  automatic: ['automaticCertificates'],
  averageSessionDuration: ['averageSessionDurationSeconds'],
  certificatesIssued: ['issued'],
  completed: ['usersCompleted'],
  eligibleCompletions: ['eligibleCourseCompletions'],
  loggedInNotWatched: ['loggedInWithoutWatch'],
  pending: ['pendingApprovals'],
  started: ['usersStarted'],
}

const resolveKpiValue = (data, key) => {
  const sources = [data?.kpis || {}, data || {}]
  const keys = [key, ...(KPI_ALIASES[key] || [])]
  for (const source of sources) {
    for (const candidate of keys) {
      if (cleanValue(source?.[candidate]) !== null) return source[candidate]
    }
  }
  return null
}

const semanticKey = value => String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, '')

const localizeDatumLabel = (datum, index, formatters, t, semanticLabels = []) => {
  const raw = datum?.label ?? datum?.key ?? datum?.title ?? datum?.name ?? datum?.persona ?? datum?.locale ?? datum?.date ?? datum?.period ?? datum?.action ?? datum?.portal
  if (datum?.date && raw === datum.date) return formatters.date(raw)
  const mappedKey = semanticLabels[index] || SEMANTIC_LABEL_KEYS[semanticKey(raw)]
  if (mappedKey) return tr(t, mappedKey, raw === null || raw === undefined ? String(index + 1) : String(raw))
  const activityBucket = String(raw || '').match(/^(.+?)\s+activities$/i)
  if (activityBucket) return `${activityBucket[1]} ${tr(t, 'analytics_column_activities', 'activities').toLocaleLowerCase()}`
  return raw === null || raw === undefined ? String(index + 1) : String(raw)
}

const normalizeSeries = (source, valueKeys, formatters, t, semanticLabels = []) => {
  if (!source) return []
  const rows = Array.isArray(source)
    ? source.map(value => typeof value === 'number' ? { value } : value)
    : Object.entries(source).map(([label, value]) => typeof value === 'object' ? { label, ...value } : { label, value })

  return valueKeys.map((key, seriesIndex) => ({
    key,
    label: tr(t, SERIES_LABEL_KEYS[key], key),
    categorical: rows.every(row => !row.date && !row.period),
    showAllLabels: semanticLabels.length > 0 || (rows.length <= 6 && rows.every(row => !row.date && !row.period)),
    color: [COLORS.blue, COLORS.blueLight, [103, 78, 167], [24, 121, 111], COLORS.muted][seriesIndex % 5],
    values: rows.map((datum, index) => ({
      label: localizeDatumLabel(datum, index, formatters, t, semanticLabels),
      value: Number(datum?.[key] ?? (seriesIndex === 0 ? datum?.value : 0) ?? 0),
    })),
  })).filter(series => series.values.some(item => Number.isFinite(item.value) && item.value !== 0))
}

const safeFileName = value => String(value || 'analytics-report')
  .normalize('NFKD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-zA-Z0-9_-]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .toLowerCase()

const filterValues = value => {
  if (Array.isArray(value)) return value.filter(Boolean).join(', ')
  if (value && typeof value === 'object') return value.label || value.title || value.name || value.value || ''
  return cleanValue(value) ?? ''
}

const localizeFilterValue = (key, value, t) => {
  const filtered = filterValues(value)
  if (!['days', 'period'].includes(key)) return filtered
  const periodKey = PERIOD_LABEL_KEYS[semanticKey(filtered)]
  return periodKey ? tr(t, periodKey, String(filtered)) : filtered
}

const buildFilterLines = (filters, t, filterOptions = {}) => {
  const labels = {
    category: 'analytics_filter_category',
    contentType: 'analytics_filter_content_type',
    course: 'analytics_filter_course',
    dateFrom: 'analytics_filter_from',
    dateTo: 'analytics_filter_to',
    days: 'analytics_filter_period',
    event: 'analytics_filter_event',
    user: 'analytics_filter_user',
    content: 'analytics_filter_content',
    from: 'analytics_filter_from',
    locale: 'analytics_filter_locale',
    period: 'analytics_filter_period',
    persona: 'analytics_filter_persona',
    portal: 'analytics_filter_program_portal',
    program: 'analytics_filter_program_portal',
    survey: 'analytics_filter_survey',
    area: 'analytics_filter_area',
    range: 'analytics_filter_period',
    to: 'analytics_filter_to',
  }

  return Object.entries(filters || {})
    .filter(([key, value]) => !TECHNICAL_FILTER_KEYS.has(key) && filterValues(value) !== '')
    .map(([key, value]) => {
      const choices = filterOptions[{ persona: 'personas', course: 'courses', category: 'categories', program: 'programs', survey: 'surveys', area: 'areas', event: 'events' }[key]] || []
      const option = choices.find(item => String(item.id) === String(value))
      return `${tr(t, labels[key], key)}: ${localizeFilterValue(key, option?.title || value, t)}`
    })
}

export const formatAnalyticsReportFilters = (filters, t, filterOptions) => buildFilterLines(filters, t, filterOptions)

export const buildAnalyticsReportSeries = ({ source, valueKeys, locale = 'en-US', t, semanticLabels = [] }) => (
  normalizeSeries(source, valueKeys, makeFormatters(locale), t, semanticLabels)
)

const setText = (pdf, size = 10, style = 'normal', color = COLORS.ink) => {
  pdf.setFont(pdf.__analyticsFont || 'helvetica', pdf.__analyticsFont ? 'normal' : style)
  pdf.setFontSize(size)
  pdf.setTextColor(...color)
}

const arrayBufferToBase64 = arrayBuffer => {
  const bytes = new Uint8Array(arrayBuffer)
  let binary = ''
  for (let index = 0; index < bytes.length; index += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(index, index + 0x8000))
  }
  if (typeof window !== 'undefined' && typeof window.btoa === 'function') return window.btoa(binary)
  if (typeof Buffer !== 'undefined') return Buffer.from(binary, 'binary').toString('base64')
  throw new Error('Base64 encoding is unavailable')
}

const loadUnicodeFont = async (pdf, locale, fontDataLoader) => {
  const font = UNICODE_FONTS[locale]
  if (!font) return
  const loader = fontDataLoader || (async path => {
    const response = await fetch(path)
    if (!response.ok) throw new Error(`Font request failed with ${response.status}`)
    return response.arrayBuffer()
  })

  try {
    const data = await loader(font.path)
    pdf.addFileToVFS(font.file, arrayBufferToBase64(data))
    pdf.addFont(font.file, font.family, 'normal')
    pdf.__analyticsFont = font.family
  } catch (error) {
    throw new Error(`Could not load the Unicode Analytics PDF font for ${locale}: ${error.message}`)
  }
}

const drawBrandMark = (pdf, x, y, compact = false) => {
  pdf.setFillColor(...COLORS.blue)
  pdf.roundedRect(x, y, compact ? 26 : 36, compact ? 5 : 7, 1.5, 1.5, 'F')
  setText(pdf, compact ? 7 : 10, 'bold', COLORS.white)
  pdf.text('MAGVENTURE', x + (compact ? 13 : 18), y + (compact ? 3.5 : 4.8), { align: 'center' })
}

const drawPageHeader = (pdf, title, subtitle) => {
  drawBrandMark(pdf, 15, 10, true)
  setText(pdf, 18, 'bold')
  pdf.text(title, 15, 27)
  setText(pdf, 8.5, 'normal', COLORS.muted)
  pdf.text(pdf.splitTextToSize(subtitle || '', 258), 15, 33)
  pdf.setDrawColor(...COLORS.border)
  pdf.line(15, 41, 282, 41)
  return 48
}

const addPage = (pdf, title, subtitle) => {
  pdf.addPage('a4', 'landscape')
  return drawPageHeader(pdf, title, subtitle)
}

const ensureSpace = (pdf, y, required, title, subtitle) => (
  y + required > 190 ? addPage(pdf, title, subtitle) : y
)

const drawKpis = (pdf, y, metrics, formatters) => {
  if (!metrics.length) return y
  const columns = 4
  const width = 64.5
  const height = 22
  metrics.forEach((metric, index) => {
    const column = index % columns
    const row = Math.floor(index / columns)
    const x = 15 + column * 67.5
    const top = y + row * 25
    pdf.setFillColor(...COLORS.pale)
    pdf.roundedRect(x, top, width, height, 2, 2, 'F')
    setText(pdf, 7.2, 'bold', COLORS.muted)
    pdf.text(pdf.splitTextToSize(metric.label.toUpperCase(), width - 8).slice(0, 2), x + 4, top + 6)
    setText(pdf, 15, 'bold')
    pdf.text(formatMetric(formatters, metric.value, metric.type), x + 4, top + 17)
  })
  return y + Math.ceil(metrics.length / columns) * 25 + 4
}

const drawChart = (pdf, y, title, series, formatters) => {
  if (!series.length || !series[0].values.length) return y
  const x = 15
  const width = 267
  const height = 78
  const plotX = x + 17
  const plotY = y + 23
  const plotWidth = width - 25
  const plotHeight = 31
  const values = series.flatMap(item => item.values.map(point => point.value))
  const max = Math.max(1, ...values)

  pdf.setFillColor(...COLORS.pale)
  pdf.roundedRect(x, y, width, height, 2, 2, 'F')
  setText(pdf, 9, 'bold')
  pdf.text(title, x + 5, y + 7)
  series.forEach((item, index) => {
    pdf.setFillColor(...item.color)
    pdf.circle(x + 6 + (index % 3) * 87, y + 13 + Math.floor(index / 3) * 5, 1.2, 'F')
    setText(pdf, 6.5, 'normal', COLORS.muted)
    pdf.text(item.label, x + 9 + (index % 3) * 87, y + 14 + Math.floor(index / 3) * 5)
  })

  ;[0, 0.5, 1].forEach(fraction => {
    const tickY = plotY + plotHeight * (1 - fraction)
    pdf.setDrawColor(...COLORS.border)
    pdf.setLineWidth(0.15)
    pdf.line(plotX, tickY, plotX + plotWidth, tickY)
    setText(pdf, 6.3, 'normal', COLORS.muted)
    pdf.text(formatters.decimal(max * fraction), plotX - 2, tickY + 1, { align: 'right' })
  })
  const pointCount = series[0].values.length
  const step = pointCount > 1 ? plotWidth / (pointCount - 1) : plotWidth

  series.forEach(item => {
    pdf.setDrawColor(...item.color)
    pdf.setLineWidth(0.8)
    item.values.forEach((point, index) => {
      const pointX = plotX + (pointCount === 1 ? plotWidth / 2 : index * step)
      const pointY = plotY + plotHeight - (Math.max(0, point.value) / max) * plotHeight
      if (index > 0) {
        const previous = item.values[index - 1]
        const previousX = plotX + (index - 1) * step
        const previousY = plotY + plotHeight - (Math.max(0, previous.value) / max) * plotHeight
        pdf.line(previousX, previousY, pointX, pointY)
      }
      pdf.setFillColor(...item.color)
      pdf.circle(pointX, pointY, 1.2, 'F')
    })
  })

  const labelIndexes = (series[0].showAllLabels
    ? Array.from({ length: pointCount }, (_, index) => index)
    : [0, Math.floor((pointCount - 1) / 2), pointCount - 1]
  ).filter((value, index, list) => value >= 0 && list.indexOf(value) === index)
  setText(pdf, 6.3, 'normal', COLORS.muted)
  labelIndexes.forEach(index => {
    const labelX = plotX + (pointCount === 1 ? plotWidth / 2 : index * step)
    pdf.text(pdf.splitTextToSize(String(series[0].values[index]?.label || ''), Math.min(75, Math.max(25, step - 3))), labelX, plotY + plotHeight + 6, { align: index === 0 ? 'left' : index === pointCount - 1 ? 'right' : 'center' })
  })
  return y + height + 5
}

const drawTableHeader = (pdf, y, columns, widths) => {
  setText(pdf, 7, 'bold', COLORS.white)
  const labels = columns.map((column, index) => pdf.splitTextToSize(column.label, widths[index] - 4))
  const height = Math.max(...labels.map(lines => lines.length)) * 3.3 + 5
  pdf.setFillColor(...COLORS.blue)
  pdf.rect(15, y, 267, height, 'F')
  let x = 17
  labels.forEach((lines, index) => {
    pdf.text(lines, x, y + 4.2, { lineHeightFactor: 1.33 })
    x += widths[index]
  })
  return y + height
}

const drawTable = (pdf, y, title, columns, rows, formatters, pageTitle, pageSubtitle) => {
  if (!rows.length) return y
  const firstWidth = columns.length > 4 ? 70 : 92
  const remaining = (267 - firstWidth) / Math.max(1, columns.length - 1)
  const widths = columns.map((column, index) => index === 0 ? firstWidth : remaining)
  y = ensureSpace(pdf, y, 25, pageTitle, pageSubtitle)
  setText(pdf, 9, 'bold')
  pdf.text(title, 15, y + 5)
  y = drawTableHeader(pdf, y + 8, columns, widths)

  rows.forEach((row, rowIndex) => {
    setText(pdf, 7, 'normal', COLORS.ink)
    const cells = columns.map((column, index) => {
      const keys = Array.isArray(column.key) ? column.key : [column.key]
      const raw = keys.map(key => row?.[key]).find(value => cleanValue(value) !== null)
      return pdf.splitTextToSize(column.type ? formatMetric(formatters, raw, column.type) : String(cleanValue(raw) ?? '-'), widths[index] - 4)
    })
    const lineCount = Math.max(...cells.map(lines => lines.length), 1)
    let offset = 0
    while (offset < lineCount) {
      if (y + Math.min(lineCount - offset, 4) * 3.3 + 5 > 190) {
        y = addPage(pdf, pageTitle, pageSubtitle)
        y = drawTableHeader(pdf, y, columns, widths)
      }
      const count = Math.min(lineCount - offset, Math.max(1, Math.floor((190 - y - 5) / 3.3)))
      const height = count * 3.3 + 5
      if (rowIndex % 2 === 0) {
        pdf.setFillColor(...COLORS.pale)
        pdf.rect(15, y, 267, height, 'F')
      }
      let x = 17
      setText(pdf, 7, 'normal', COLORS.ink)
      cells.forEach((lines, index) => {
        pdf.text(lines.slice(offset, offset + count), x, y + 4.2, { lineHeightFactor: 1.33 })
        x += widths[index]
      })
      y += height
      offset += count
    }
  })
  return y + 5
}

export const prepareAnalyticsReportData = (section, payload, t) => {
  const data = unwrapData(payload)
  const overview = section === 'activity' ? unwrapData(data.activityOverview) : data
  return {
    ...data,
    ...(section === 'activity' ? { kpis: { ...overview.kpis, ...data.kpis }, timeline: overview.timeline, portalUsage: overview.portalUsage } : {}),
    personaUsage: (overview.personaUsage || []).map(row => ({ ...row, ...row.activityTypes })),
    competitiveHubUsage: (data.competitiveHubUsage || []).map(row => ({
      ...row,
      sectionLabel: tr(t, { research: 'competitive_intelligence_nav_research', market: 'competitive_intelligence_nav_market', assistants: 'competitive_intelligence_nav_assistants', overview: 'competitive_intelligence_nav_overview' }[String(row.pagePath || '').split('/').pop()], row.pagePath),
    })),
    onboardingFunnel: data.onboarding?.funnel || [],
    certificateJourney: data.certificates ? [
      { key: 'completed', value: resolveKpiValue(data.certificates, 'eligibleCompletions') || 0 },
      { key: 'certified', value: resolveKpiValue(data.certificates, 'certificatesIssued') || 0 },
    ] : [],
  }
}

const sectionMetrics = (config, data, t) => {
  const seen = new Set()
  return config.kpis.reduce((metrics, [key, label, type]) => {
    const value = resolveKpiValue(data, key)
    const translatedLabel = tr(t, label)
    if (value !== null && !seen.has(translatedLabel)) {
      seen.add(translatedLabel)
      metrics.push({ key, label: translatedLabel, type, value })
    }
    return metrics
  }, [])
}

const drawSection = (pdf, section, payload, t, formatters, useFirstPage = false) => {
  const config = SECTION_CONFIG[section]
  if (!config) return
  const data = prepareAnalyticsReportData(section, payload, t)
  const title = tr(t, config.title, section)
  const subtitle = tr(t, config.description, '')
  let y = useFirstPage ? drawPageHeader(pdf, title, subtitle) : addPage(pdf, title, subtitle)
  y = drawKpis(pdf, y, sectionMetrics(config, data, t), formatters)
  if (data.dataQuality?.truncated) {
    setText(pdf, 8, 'normal', COLORS.muted)
    const lines = pdf.splitTextToSize(tr(t, 'analytics_data_truncated'), 267)
    pdf.text(lines, 15, y + 3)
    y += lines.length * 4 + 7
  }

  config.charts.forEach(([key, label, valueKeys, semanticLabels]) => {
    const series = normalizeSeries(data?.[key], valueKeys, formatters, t, semanticLabels)
    if (series.length) {
      y = ensureSpace(pdf, y, 84, title, subtitle)
      y = drawChart(pdf, y, tr(t, label, key), series, formatters)
      if (series[0].categorical) {
        y = drawTable(pdf, y, tr(t, label, key), [
          { key: 'label', label: tr(t, label, key) },
          ...series.map(item => ({ key: item.key, label: item.label, type: 'number' })),
        ], series[0].values.map((point, index) => ({
          label: point.label,
          ...Object.fromEntries(series.map(item => [item.key, item.values[index]?.value ?? 0])),
        })), formatters, title, subtitle)
      }
    }
  })

  config.tables.forEach(([key, label, definitions]) => {
    const rows = Array.isArray(data?.[key]) ? data[key] : []
    if (rows.length) {
      y = drawTable(
        pdf,
        y,
        tr(t, label, key),
        definitions.map(([columnKey, columnLabel, type]) => ({ key: columnKey, label: tr(t, columnLabel, columnKey), type })),
        rows,
        formatters,
        title,
        subtitle,
      )
    }
  })

  if (!sectionMetrics(config, data, t).length && !config.charts.some(([key]) => data?.[key]?.length) && !config.tables.some(([key]) => data?.[key]?.length)) {
    setText(pdf, 12, 'normal', COLORS.muted)
    pdf.text(tr(t, 'analytics_no_data_description', 'No data is available for the selected filters.'), 15, y + 12)
  }
}

const drawCover = (pdf, { mode, currentSection, filters, filterOptions, generatedAt, t, formatters }) => {
  pdf.setFillColor(...COLORS.background)
  pdf.rect(0, 0, 297, 210, 'F')
  drawBrandMark(pdf, 18, 18)
  setText(pdf, 10, 'bold', COLORS.blueLight)
  pdf.text(tr(t, 'analytics_pdf_brand_line', 'MAGVENTURE UNIVERSE'), 18, 59)
  setText(pdf, 29, 'bold', COLORS.white)
  pdf.text(tr(t, mode === 'full' ? 'analytics_pdf_full_title' : 'analytics_pdf_current_title', 'Analytics Report'), 18, 76)
  setText(pdf, 12, 'normal', [190, 202, 223])
  pdf.text(
    pdf.splitTextToSize(tr(t, mode === 'full' ? 'analytics_pdf_full_description' : 'analytics_pdf_current_description', 'Filtered analytics prepared for review.'), 260),
    18,
    87,
  )
  if (mode === 'current') {
    setText(pdf, 11, 'bold', COLORS.white)
    pdf.text(tr(t, SECTION_CONFIG[currentSection]?.title, currentSection), 18, 101)
  }
  setText(pdf, 8, 'bold', COLORS.blueLight)
  pdf.text(tr(t, 'analytics_pdf_filters', 'FILTERS'), 18, 121)
  setText(pdf, 8.5, 'normal', [210, 218, 233])
  const filterLines = buildFilterLines(filters, t, filterOptions)
  const wrappedFilters = (filterLines.length ? filterLines : [tr(t, 'analytics_filters_none', 'No additional filters')]).flatMap(line => pdf.splitTextToSize(line, 260))
  pdf.text(wrappedFilters.slice(0, 12), 18, 130)
  setText(pdf, 8, 'normal', [150, 165, 191])
  pdf.text(`${tr(t, 'analytics_pdf_generated_at', 'Generated at')}: ${formatters.dateTime(generatedAt)}`, 18, 191)
  for (let offset = 12; offset < wrappedFilters.length; offset += 32) {
    const y = addPage(pdf, tr(t, 'analytics_pdf_filters', 'Filters'), '')
    setText(pdf, 9, 'normal')
    pdf.text(wrappedFilters.slice(offset, offset + 32), 15, y)
  }
}

const drawExecutiveSummary = (pdf, overview, t, formatters) => {
  const config = SECTION_CONFIG.overview
  const data = unwrapData(overview)
  let y = addPage(
    pdf,
    tr(t, 'analytics_pdf_executive_summary', 'Executive Summary'),
    tr(t, 'analytics_pdf_executive_summary_description', 'A concise view of Universe adoption and content engagement.'),
  )
  y = drawKpis(pdf, y, sectionMetrics(config, data, t).slice(0, 8), formatters)
  const series = normalizeSeries(data?.timeline, ['activeUsers', 'sessions', 'activities', 'events'], formatters, t)
  if (series.length) drawChart(pdf, y, tr(t, 'analytics_chart_usage_over_time', 'Usage over time'), series, formatters)
}

const addFooters = (pdf, t, generatedAt, formatters) => {
  const pages = pdf.internal.getNumberOfPages()
  for (let page = 1; page <= pages; page += 1) {
    pdf.setPage(page)
    if (page > 1) {
      pdf.setDrawColor(...COLORS.border)
      pdf.line(15, 198, 282, 198)
      setText(pdf, 6.5, 'normal', COLORS.muted)
      pdf.text(`MagVenture Universe Analytics - ${formatters.dateTime(generatedAt)}`, 15, 203)
      pdf.text(`${tr(t, 'analytics_pdf_page', 'Page')} ${page} / ${pages}`, 282, 203, { align: 'right' })
    }
  }
}

export const generateAnalyticsReport = async ({
  mode = 'current',
  section = 'overview',
  sectionData,
  filters = {},
  filterOptions = {},
  language = 'English',
  locale,
  t,
  loadSection,
  generatedAt = new Date(),
  fontDataLoader,
  save = true,
} = {}) => {
  if (!['current', 'full'].includes(mode)) throw new Error('Invalid analytics report mode')
  if (mode === 'current' && !SECTION_CONFIG[section]) throw new Error('Invalid analytics report section')
  if (mode === 'full' && typeof loadSection !== 'function') throw new Error('Full analytics report requires loadSection')

  const resolvedLocale = locale || resolveAnalyticsLocale(language)
  const formatters = makeFormatters(resolvedLocale)
  const reportGeneratedAt = generatedAt instanceof Date ? generatedAt : new Date(generatedAt)
  const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4', compress: true, putOnlyUsedFonts: true })
  const sections = mode === 'full'
    ? Object.fromEntries(await Promise.all(ANALYTICS_REPORT_SECTIONS.map(async sectionId => [sectionId, await loadSection(sectionId)])))
    : { [section]: typeof loadSection === 'function' ? await loadSection(section) : sectionData || {} }
  if (mode === 'current' && section === 'overview' && typeof loadSection === 'function') {
    const [onboarding, certificates] = await Promise.all(['onboarding', 'certificates'].map(sectionId => loadSection(sectionId)))
    sections.overview = { ...unwrapData(sections.overview), onboarding: unwrapData(onboarding), certificates: unwrapData(certificates) }
  }
  if (mode === 'current' && section === 'activity' && typeof loadSection === 'function') {
    sections.activity = { ...unwrapData(sections.activity), activityOverview: unwrapData(await loadSection('overview')) }
  }
  if (mode === 'full') {
    sections.overview = { ...unwrapData(sections.overview), onboarding: unwrapData(sections.onboarding), certificates: unwrapData(sections.certificates) }
    sections.activity = { ...unwrapData(sections.activity), activityOverview: sections.overview }
  }

  // Content can use a different script from the selected interface language.
  const fontLocale = /[\u2e80-\u9fff\uf900-\ufaff]/.test(JSON.stringify(sections)) ? 'cjk-content' : resolvedLocale
  await loadUnicodeFont(pdf, fontLocale, fontDataLoader)

  drawCover(pdf, { mode, currentSection: section, filters, filterOptions, generatedAt: reportGeneratedAt, t, formatters })
  if (mode === 'full') {
    drawExecutiveSummary(pdf, sections.overview, t, formatters)
    ANALYTICS_REPORT_SECTIONS.forEach(sectionId => drawSection(pdf, sectionId, sections[sectionId], t, formatters))
  } else {
    drawSection(pdf, section, sections[section], t, formatters)
  }
  addFooters(pdf, t, reportGeneratedAt, formatters)

  const filename = `${safeFileName(tr(t, mode === 'full' ? 'analytics_pdf_full_filename' : 'analytics_pdf_current_filename', 'magventure-analytics'))}-${reportGeneratedAt.toISOString().slice(0, 10)}.pdf`
  if (save) pdf.save(filename)

  return {
    blob: pdf.output('blob'),
    filename,
    pageCount: pdf.internal.getNumberOfPages(),
    pdf,
  }
}
